import axios from 'axios';
import { Booking, Student, ApiResponse, ManualStudentInvitePreview } from '@court-sync/shared';
import { API_BASE_URL } from '../config/api';
import { auth } from '../config/firebase';

export interface CreateSocialBookingData {
  lessonId: string;
  termId?: string;
  academyId: string;
  /** When provided, uses existing student instead of creating new one. studentData is ignored. */
  studentId?: string;
  /** 'myself' = self-student (adult), 'child' = guardian booking for child. Used when creating new student. */
  bookingFor?: 'myself' | 'child';
  studentData?: {
    firstName: string;
    lastName: string;
    dateOfBirth: Date;
    gender: 'male' | 'female' | 'other';
    skillLevel?: 'beginner' | 'intermediate' | 'advanced';
    medicalNotes?: string;
    emergencyContact?: {
      name: string;
      phone: string;
      relationship: string;
    };
  };
  userId: string; // Firebase user ID from social login
  customerFirstName?: string; // First name of person making the booking
  customerLastName?: string; // Last name of person making the booking
  customerEmail?: string; // Email of person making the booking
  /** When claiming a waitlist spot (from SpotAvailable email). Skips capacity check. */
  waitlistEntryId?: string;
  /** For per-session clinic bookings: the specific session date (YYYY-MM-DD) being booked. */
  sessionDate?: string;
  /** Invitation token from EOI approval email (?invitation=) for invitation-only programs. */
  invitationToken?: string;
  /** Manual roster invite token from staff email (?studentInvite=). Links staff-created student row on signup. */
  manualInviteToken?: string;
}

export const publicBookingService = {
  /** Valid manual-invite token → roster student context for signup (404 if invalid/expired). */
  async getManualInvitePreview(token: string): Promise<ApiResponse<ManualStudentInvitePreview>> {
    const enc = encodeURIComponent(token.trim());
    const response = await axios.get(`${API_BASE_URL}/public/bookings/manual-invite/${enc}`);
    return response.data;
  },

  async createSocialBooking(bookingData: CreateSocialBookingData): Promise<ApiResponse<{ booking: Booking; student: Student }>> {
    const response = await axios.post(`${API_BASE_URL}/public/bookings/social`, bookingData);
    return response.data;
  },

  async getLessonStudentEnrollments(lessonId: string): Promise<
    ApiResponse<{
      enrollments: Array<{
        studentId: string;
        firstName: string;
        lastName: string;
        isSelf: boolean;
        sessionDates: string[];
        hasFullTerm: boolean;
      }>;
    }>
  > {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      return { success: false, error: 'Not authenticated' };
    }
    const token = await firebaseUser.getIdToken();
    const response = await axios.get(
      `${API_BASE_URL}/public/bookings/lesson/${encodeURIComponent(lessonId)}/enrollments`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  async createPendingBooking(bookingData: {
    lessonId: string;
    termId: string;
    academyId: string;
    studentData: {
      firstName: string;
      lastName: string;
      dateOfBirth: Date;
      gender: 'male' | 'female' | 'other';
      skillLevel?: 'beginner' | 'intermediate' | 'advanced';
      medicalNotes?: string;
      emergencyContact?: {
        name: string;
        phone: string;
        relationship: string;
      };
    };
    userData: {
      email: string;
      firstName: string;
      lastName: string;
      phone?: string;
    };
  }): Promise<ApiResponse<{ bookingId: string; pendingToken: string }>> {
    const response = await axios.post(`${API_BASE_URL}/public/bookings/pending`, bookingData);
    return response.data;
  },

  async claimBooking(bookingId: string): Promise<ApiResponse<{ booking: Booking }>> {
    // Get auth token for authenticated request
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to claim a booking');
    }
    
    const token = await auth.currentUser.getIdToken();
    const response = await axios.post(
      `${API_BASE_URL}/public/bookings/${bookingId}/claim`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },
};

